import { Controller } from "@hotwired/stimulus"
import intlTelInput from "intl-tel-input"
import { forbiddenNanp911E164 } from "../utils/phone_number_rules"

export default class extends Controller {
  static targets = [
    "name",
    "email",
    "phoneIti",
    "phoneHidden",
    "phoneError",
    "businessRole",
    "agreement",
    "submit",
    "loadingOverlay",
  ]
  static values = {
    excludedCountries: { type: Array, default: [] },
  }

  connect() {
    this.captchaReady = false
    this.iti = null
    this.boundIntlSync = null
    this.phoneItiHydrated = false
    this.phoneFieldTouched = false
    this.initIntlPhone()
    this.syncSubmitDisabled()
  }

  disconnect() {
    if (this.iti && this.boundIntlSync) {
      this.phoneItiTarget.removeEventListener("countrychange", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("input", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("blur", this.boundIntlSync)
      this.iti.destroy()
      this.iti = null
    }
  }

  captchaVerified(event) {
    this.captchaReady = Boolean(event.detail?.token)
    this.syncSubmitDisabled()
  }

  captchaCleared() {
    this.captchaReady = false
    this.syncSubmitDisabled()
  }

  hideLoading() {
    if (!this.hasLoadingOverlayTarget) return
    this.loadingOverlayTarget.classList.add("hidden")
    this.loadingOverlayTarget.classList.remove("flex")
    this.loadingOverlayTarget.setAttribute("aria-busy", "false")
  }

  initIntlPhone() {
    this.boundIntlSync = () => {
      this.phoneFieldTouched = true
      this.syncIntlPhoneToHidden()
      this.syncSubmitDisabled()
    }

    try {
      this.iti = intlTelInput(this.phoneItiTarget, {
        initialCountry: "us",
        countryOrder: ["us"],
        excludeCountries: this.excludedCountriesValue,
        loadUtils: () => import("intl-tel-input/utils"),
        customPlaceholder: (nationalExample, countryData) => {
          const prefix = countryData?.dialCodePlus
          if (!prefix) return nationalExample || ""
          if (!nationalExample) return prefix
          return `${prefix} ${nationalExample}`
        },
      })

      this.phoneItiTarget.addEventListener("countrychange", this.boundIntlSync)
      this.phoneItiTarget.addEventListener("input", this.boundIntlSync)
      this.phoneItiTarget.addEventListener("blur", this.boundIntlSync)

      this.iti.promise
        .then(() => {
          this.restoreIntlFromHidden()
          this.phoneItiHydrated = true
          this.syncSubmitDisabled()
        })
        .finally(() => {
          this.hideLoading()
        })
    } catch (_error) {
      this.phoneItiHydrated = true
      this.hideLoading()
    }
  }

  restoreIntlFromHidden() {
    const v = this.phoneHiddenTarget.value.trim()
    if (!v || !this.iti) return
    this.iti.setNumber(v)
  }

  syncIntlPhoneToHidden() {
    if (!this.iti) return
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") {
      // Keep a submitted E.164 value until ITI has restored into the visible field
      // (e.g. after a validation re-render).
      if (!this.phoneItiHydrated && this.phoneHiddenTarget.value.trim() !== "") return
      this.phoneHiddenTarget.value = ""
      return
    }
    if (!intlTelInput.utils) return
    const e164 = this.iti.getNumber()
    this.phoneHiddenTarget.value = e164 || ""
  }

  phoneFieldState() {
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") {
      return { valid: false, error: "Enter the main business phone number." }
    }
    if (!this.iti || !intlTelInput.utils) return { valid: false, error: "" }

    this.syncIntlPhoneToHidden()
    const e164 = this.phoneHiddenTarget.value.trim()
    if (forbiddenNanp911E164(e164)) {
      return {
        valid: false,
        error: "911 cannot be used in the area code or as the first three digits of the local number.",
      }
    }

    if (this.iti.isValidNumber() === true) return { valid: true, error: "" }
    return { valid: false, error: "Enter a valid phone number for the selected country." }
  }

  applyPhoneErrorFeedback(message) {
    const show = message !== ""

    this.phoneErrorTarget.textContent = message
    this.phoneErrorTarget.classList.toggle("hidden", !show)
    if (show) {
      this.phoneItiTarget.setAttribute("aria-invalid", "true")
      this.phoneItiTarget.setAttribute("aria-errormessage", "thing-claim-phone-error")
    } else {
      this.phoneItiTarget.removeAttribute("aria-invalid")
      this.phoneItiTarget.removeAttribute("aria-errormessage")
    }
  }

  sync() {
    this.syncSubmitDisabled()
  }

  requiredFieldsValid(phoneValid) {
    return this.coreFieldsValid() && phoneValid
  }

  syncSubmitDisabled() {
    this.syncIntlPhoneToHidden()
    const phone = this.phoneFieldState()
    // Only show phone errors after the field has been touched, or when every
    // other required control is ready (so a blank phone isn't silent).
    const showPhoneError = this.phoneFieldTouched || this.otherRequiredFieldsValid()
    this.applyPhoneErrorFeedback(showPhoneError ? phone.error : "")
    const enable = this.requiredFieldsValid(phone.valid) && this.captchaReady
    this.submitTarget.disabled = !enable
  }

  otherRequiredFieldsValid() {
    return this.coreFieldsValid() && this.captchaReady
  }

  coreFieldsValid() {
    const nameOk = this.nameTarget.value.trim().length > 0
    const emailOk = this.emailTarget.checkValidity()
    const roleOk = this.businessRoleTarget.value.trim().length > 0
    const agreementOk = this.agreementTarget.checked
    return nameOk && emailOk && roleOk && agreementOk
  }
}
